import React, { useEffect, useState } from 'react';
import {
  deliveryTypes,
  payerTypes,
  recipientTypes,
  selfDeliveryTypes,
} from '@shared/config/delivery-options';
import CartDeliveryTypeRadios from './components/CartDeliveryTypeRadios';
import { CartSelect } from './components/CartSelect';
import { CartFieldWrp } from './components/CartFieldWrp';
import { CartInput } from './components/CartInput';
import { CartSelfDeliveryData } from './components/CartSelfDeliveryData';
import { Address, FetchStatusType } from '../../../types/interfaces';
import { apiClient } from '../../../api/instance';
import { FaPaperclip } from 'react-icons/fa';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import { setErrorNotification } from '../../../app/store/notifications';
import { Button } from '../../../shared/ui/button';
import { selectUser } from '../../../entities/auth/model';
import { getTaxLabelForCompany } from '@shared/lib/tax-label';

export { formatAddress } from '@shared/lib/format-address';

interface SelectOption {
  label: string;
  value: string;
}

interface ICartStepDeliveryMethods {
  cartRegister: any;
  cartErrors: any;
  shipmentAddress: any;
  deliveryType: SelectOption | null;
  payerType: SelectOption | null;
  setValue: any;
  recipientType: SelectOption | null;
  deliveryMode: string;
  setStepOnContacts: () => void;
  watch: (field: string) => any;
  status: FetchStatusType;
  address: any;
  selfDeliveryType: any;
  trigger: () => void;
  itemsLength: any;
}

export const CartStepDeliveryMethods: React.FC<ICartStepDeliveryMethods> = ({
  cartRegister,
  cartErrors,
  shipmentAddress,
  deliveryType,
  payerType,
  trigger,
  setValue,
  recipientType,
  deliveryMode,
  setStepOnContacts,
  watch,
  status,
  address,
  selfDeliveryType,
  itemsLength,
}) => {
  const [files, setFiles] = useState<Record<string, string>>({
    confidant_number: '',
    invoice_url: '',
  });
  // Лейбл налогового номера для блока «Третья сторона». Берём по
  // стране юр.лица текущего пользователя — если KZ-компания делает
  // заказ, форма третьей стороны будет в её терминах (БИН/ИИН), что
  // ожидаемо. Если кейс «KZ-юзер указывает RU-партнёра» окажется
  // важным — переключим на свободный «Налоговый номер».
  const me = useAppSelector(selectUser);
  const taxLabel = getTaxLabelForCompany(me?.company);
  console.log(itemsLength);
  const setDeliveryMode = (value: string) => {
    setValue('deliveryMode', value);
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileUrl = await uploadFile(file);
      setValue(type, fileUrl);
      setFiles((prevFiles) => ({ ...prevFiles, [type]: fileUrl }));
      trigger();
    }
  };

  const dispatch = useAppDispatch();

  const uploadFile = async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await apiClient.post('/files/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.file;
    } catch (error) {
      dispatch(setErrorNotification('Ошибка при загрузке файла'));
    }
  };

  useEffect(() => {
    trigger();
  }, [watch('add_pickup_details_later')]);

  const renderFileUpload = (type: string) => (
    <>
      <input
        type="file"
        id={`file_input_${type}`}
        className="hidden"
        onChange={(e) => handleFileChange(e, type)}
      />
      <label
        htmlFor={`file_input_${type}`}
        className={`cursor-pointer ${watch('add_pickup_details_later') ? 'invisible' : 'visible'
          }`}
      >
        <FaPaperclip className="text-xl text-red-600" />
      </label>
    </>
  );

  const deliveryTypeContent = () => (
    <div className="flex gap-6">
      <div className="flex-1">
        <CartFieldWrp title={'Адрес доставки'}>
          <CartSelect
            options={address}
            onChange={(item: Address) => setValue('shipmentAddress', item)}
            value={shipmentAddress}
            placeholder={'Выберите адрес доставки'}
          />
        </CartFieldWrp>
        <CartFieldWrp title={'Кто оплачивает доставку?'}>
          <CartSelect
            options={payerTypes.map((i) => {
              if (itemsLength < 10 && i.value === 'sender') {
                return {
                  ...i,
                  isDisabled: true,
                };
              }
              return i;
            })}
            onChange={(item: SelectOption) => setValue('payer_type', item)}
            value={payerType}
            placeholder={'Кто оплачивает доставку?'}
          />
        </CartFieldWrp>
        <CartFieldWrp title={'На кого оформлен груз?'}>
          <CartSelect
            options={recipientTypes}
            onChange={(item: SelectOption) => setValue('recipient_type', item)}
            value={recipientType}
            placeholder={'На кого оформлен груз?'}
          />
        </CartFieldWrp>
        {recipientType?.value === 'third party' && (
          <>
            <CartFieldWrp title={'Данные компании'}>
              <CartInput
                label={`${taxLabel} *`}
                placeholder={`Введите ${taxLabel}`}
                inputName="third_party_company_inn"
                register={cartRegister}
                errors={cartErrors}
              />
            </CartFieldWrp>
            <CartFieldWrp>
              <CartInput
                label="Название компании *"
                placeholder="Название компании третьей стороны"
                inputName="third_party_company_name"
                register={cartRegister}
                errors={cartErrors}
              />
            </CartFieldWrp>
          </>
        )}
        <div id="cart_address_modal_for_portal"></div>
      </div>
      <div className="flex-1">
        {payerType?.value === 'recipient' && (
          <CartFieldWrp title={'Кто доставит заказ?'}>
            <CartSelect
              options={deliveryTypes}
              onChange={(item: SelectOption) => setValue('delivery_type', item)}
              value={deliveryType}
              placeholder={'Кто доставит заказ?'}
            />
          </CartFieldWrp>
        )}
      </div>
    </div>
  );

  const selfDeliveryTypeContent = () => (
    <div className="flex gap-6">
      <div className="flex-1">
        <CartFieldWrp title={'Кто заберет заказ?'}>
          <CartSelect
            options={selfDeliveryTypes}
            onChange={(item: SelectOption) =>
              setValue('self_delivery_type', item)
            }
            value={selfDeliveryType}
            placeholder={'Выберите компанию доставки'}
          />
        </CartFieldWrp>
        <div>
          <CartFieldWrp title="Дополнительные данные">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                id="add_pickup_details_later"
                {...cartRegister('add_pickup_details_later')}
                className="w-9 h-5 appearance-none bg-gray-300 rounded-full relative cursor-pointer transition-colors checked:bg-red-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform checked:after:translate-x-4"
              />
              <span className="text-sm text-gray-700">
                Добавить данные позже в разделе История заказов?
              </span>
            </label>
          </CartFieldWrp>
          {selfDeliveryType?.value === 'confidant' && (
            <>
              <CartFieldWrp>
                <CartInput
                  label="ФИО"
                  placeholder="ФИО доверенного лица"
                  inputName="confidant_name"
                  register={cartRegister}
                  errors={cartErrors}
                  isDisabled={watch('add_pickup_details_later')}
                />
              </CartFieldWrp>
              <CartFieldWrp>
                <CartInput
                  label="Номер доверенности или фото"
                  placeholder="Номер доверенности"
                  inputName="confidant_number"
                  register={cartRegister}
                  errors={cartErrors}
                  isDisabled={watch('add_pickup_details_later')}
                  icon={renderFileUpload('confidant_number')}
                />
              </CartFieldWrp>
            </>
          )}
          {selfDeliveryType?.value === 'courier' && (
            <>
              <CartFieldWrp>
                <CartInput
                  label="Название сервиса *"
                  placeholder="Курьерская доставка"
                  inputName="courier_service"
                  register={cartRegister}
                  errors={cartErrors}
                  isDisabled={watch('add_pickup_details_later')}
                />
              </CartFieldWrp>
              <CartFieldWrp>
                <CartInput
                  label="Номер накладной или фото"
                  placeholder="Номер накладной"
                  inputName="invoice_url"
                  register={cartRegister}
                  errors={cartErrors}
                  isDisabled={watch('add_pickup_details_later')}
                  icon={renderFileUpload('invoice_url')}
                />
              </CartFieldWrp>
            </>
          )}
        </div>
      </div>
      <div className="flex-1 py-6 px-4.5">
        <CartSelfDeliveryData />
      </div>
    </div>
  );

  return (
    <>
      <div>
        <CartDeliveryTypeRadios
          deliveryMode={deliveryMode}
          setDeliveryMode={setDeliveryMode}
        />
      </div>
      <div className="mt-6 flex-1">
        {deliveryMode === 'delivery' && deliveryTypeContent()}
        {deliveryMode === 'self-delivery' && selfDeliveryTypeContent()}
      </div>
      <div className="flex justify-end gap-3 mt-6 ">
        <div className="flex gap-3 mt-6 w-[calc(100%-24rem)]">
          <Button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setStepOnContacts();
            }}
            variant='light'
            type="button"
          >
            Назад
          </Button>
          <Button
            type="submit"
          >
            Оформить заказ
          </Button>
        </div>
      </div>
    </>
  );
};
